import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { toast } from 'sonner';

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  custom_domain: string | null;
  is_active: boolean;
  show_branding: boolean;
  logo_url: string | null;
  primary_color: string | null;
  secondary_color: string | null;
  hover_color: string | null;
  text_color: string | null;
  phone_required: boolean;
  sms_reminder_default: boolean;
  phone: string | null;
  email: string | null;
  website: string | null;
  billing_company_name: string | null;
  billing_address: string | null;
  billing_city: string | null;
  billing_postal_code: string | null;
  billing_country: string | null;
  billing_tax_id: string | null;
  billing_vat_number: string | null;
  show_billing_in_emails: boolean;
  demo_expires_at: string | null;
  subscription_plan_id: string | null;
  sms_auto_topup_enabled: boolean;
  sms_auto_topup_threshold: number;
  sms_auto_topup_package_id: string;
  stripe_customer_id: string | null;
  trial_ends_at: string | null;
  business_name?: string;
  // Custom email domain
  email_domain: string | null;
  email_domain_verified: boolean;
  email_from_name: string | null;
  email_reply_to: string | null;
  // Holiday warnings
  warn_holidays: boolean;
  // Reminder schedule
  reminder_hours_before: number;
  reminder_second_enabled: boolean;
  reminder_second_hours: number;
  // Onboarding
  is_onboarded: boolean;
  // Invoicing
  invoicing_enabled: boolean;
  invoicing_active: boolean;
  auto_receipt_on_completion: boolean;
  default_payment_method: string;
}

interface TenantContextType {
  tenant: Tenant | null;
  isLoading: boolean;
  isMainDomain: boolean;
  isUnifiedHomepage: boolean;
  refreshTenant: () => Promise<void>;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export function TenantProvider({ children }: { children: ReactNode }) {
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isMainDomain, setIsMainDomain] = useState(false);
  const [isUnifiedHomepage, setIsUnifiedHomepage] = useState(false);

  const refreshTenant = useCallback(async () => {
    if (!tenant?.id) return;
    const { data, error } = await supabase
      .from('tenants')
      .select('*')
      .eq('id', tenant.id)
      .maybeSingle();
    if (error) {
      logger.error('Error refreshing tenant', { error, component: 'TenantContext' });
      return;
    }
    if (data) {
      setTenant(data as Tenant);
    }
  }, [tenant?.id]);

  useEffect(() => {
    const resolveTenant = async () => {
      const hostname = window.location.hostname;
      logger.debug('Resolving tenant from hostname', { hostname, component: 'TenantContext' });

      const mainDomain = import.meta.env.VITE_MAIN_DOMAIN || 'enviflow.gr';

      // If localhost, IP address, or main domain, it's the main platform
      const isLocal = hostname === 'localhost' ||
                      hostname === '127.0.0.1' ||
                      /^(\d{1,3}\.){3}\d{1,3}$/.test(hostname);

      const isCrmDomain = hostname === `crm.${mainDomain}`;
      const isUnifiedHome = hostname === mainDomain || hostname === `www.${mainDomain}`;

      if (isLocal || hostname.endsWith('.vercel.app')) {
        // Dev/preview: allow ?tenant=slug and ?homepage=1 overrides
        const urlParams = new URLSearchParams(window.location.search);
        const tenantOverride = urlParams.get('tenant');
        const homepageOverride = urlParams.get('homepage');

        if (homepageOverride === '1') {
          logger.debug('Unified homepage override via query param', { component: 'TenantContext' });
          setIsUnifiedHomepage(true);
          setIsLoading(false);
          return;
        }

        if (tenantOverride) {
          logger.debug('Tenant override via query param', { slug: tenantOverride, component: 'TenantContext' });
          const { data, error } = await supabase
            .from('tenants')
            .select('*')
            .eq('slug', tenantOverride)
            .maybeSingle();

          if (error) {
            logger.error('Error fetching tenant by override slug', { error, slug: tenantOverride, component: 'TenantContext' });
          } else if (data) {
            setTenant(data as Tenant);
            setIsLoading(false);
            return;
          }
        }

        logger.debug('Main domain detected (dev/preview)', { hostname, component: 'TenantContext' });
        setIsMainDomain(true);
        setIsLoading(false);
        return;
      } else if (isUnifiedHome) {
        logger.debug('Unified homepage detected', { hostname, component: 'TenantContext' });
        setIsUnifiedHomepage(true);
        setIsLoading(false);
        return;
      } else if (isCrmDomain) {
        logger.debug('CRM domain detected', { hostname, component: 'TenantContext' });
        setIsMainDomain(true);
        setIsLoading(false);
        return;
      }

      // Check if it's a subdomain: {slug}.maindomain.com
      if (hostname.endsWith(`.${mainDomain}`)) {
        const slug = hostname.replace(`.${mainDomain}`, '');
        const { data, error } = await supabase
          .from('tenants')
          .select('*')
          .eq('slug', slug)
          .maybeSingle();

        if (error) {
          logger.error('Error fetching tenant by slug', { error, slug, hostname, component: 'TenantContext' });
          toast.error('Unable to load workspace. Please check your URL.');
        } else if (data) {
          setTenant(data as Tenant);
        } else {
          logger.warn('No tenant found for slug', { slug, hostname, component: 'TenantContext' });
          toast.error('Workspace not found. Please check your URL.');
        }
      } else {
        // Check for custom domain (both with and without www)
        const pureHostname = hostname.startsWith('www.') ? hostname.substring(4) : hostname;

        // Sanitize hostnames to prevent PostgREST filter injection
        const sanitizeHostname = (h: string): string => h.replace(/[^a-zA-Z0-9.\-]/g, '');
        const safeHostname = sanitizeHostname(hostname);
        const safePureHostname = sanitizeHostname(pureHostname);

        const { data, error } = await supabase
          .from('tenants')
          .select('*')
          .or(`custom_domain.eq.${safeHostname},custom_domain.eq.${safePureHostname}`)
          .maybeSingle();

        if (error) {
          logger.error('Error fetching tenant by custom domain', { error, hostname, pureHostname, component: 'TenantContext' });
          toast.error('Unable to load workspace. Please check your URL.');
        } else if (data) {
          setTenant(data as Tenant);
        } else {
          logger.warn('No tenant found for custom domain', { hostname, pureHostname, component: 'TenantContext' });
          toast.error('Workspace not found. Please check your URL.');
        }
      }

      setIsLoading(false);
    };

    resolveTenant();
  }, []);

  return (
    <TenantContext.Provider value={{ tenant, isLoading, isMainDomain, isUnifiedHomepage, refreshTenant }}>
      {!isLoading && tenant && !tenant.is_active ? (
        <div className="min-h-screen flex items-center justify-center bg-background p-4 text-center">
          <div className="max-w-md space-y-4">
            <h1 className="text-4xl font-bold text-destructive">Λογαριασμός Ανασταλμένος</h1>
            <p className="text-muted-foreground">
              Αυτός ο λογαριασμός οργανισμού έχει ανασταλεί προσωρινά. Παρακαλούμε επικοινωνήστε με την υποστήριξη στο <strong>{import.meta.env.VITE_SUPPORT_EMAIL || 'support@enviflow.gr'}</strong> για περισσότερες πληροφορίες.
            </p>
          </div>
        </div>
      ) : children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}

